<template>
  <div class="interactive-demo-container">
    <!-- Demo Interface -->
    <div 
      class="demo-interface"
      :class="{ 'is-playing': isPlaying, 'is-completed': isCompleted }"
      @click="toggleDemo"
    >
      <!-- Progress Bar -->
      <div class="progress-container">
        <div 
          class="progress-bar"
          :style="{ width: progress + '%' }"
        ></div>
      </div>

      <!-- Demo Steps Visualization -->
      <div class="demo-steps">
        <div 
          v-for="(step, index) in demoSteps" 
          :key="index"
          class="demo-step"
          :class="{ 
            'active': currentStep === index,
            'completed': currentStep > index,
            'upcoming': currentStep < index
          }"
        >
          <div class="step-icon">
            <component :is="step.icon" class="w-6 h-6" />
          </div>
          <div class="step-content">
            <h4 class="step-title">{{ step.title }}</h4>
            <p class="step-description">{{ step.description }}</p>
          </div>
          <div class="step-indicator">
            <div class="indicator-dot"></div>
          </div>
        </div>
      </div>

      <!-- Play/Pause Control -->
      <div class="demo-controls">
        <button 
          class="play-button"
          :class="{ 'playing': isPlaying }"
          @click.stop="toggleDemo"
          :aria-label="isPlaying ? 'Pause Demo' : 'Play Demo'"
        >
          <svg v-if="!isPlaying" class="play-icon" viewBox="0 0 24 24" fill="currentColor">
            <path d="M8 5v14l11-7z"/>
          </svg>
          <svg v-else class="pause-icon" viewBox="0 0 24 24" fill="currentColor">
            <path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z"/>
          </svg>
        </button>
        
        <div class="demo-info">
          <span class="demo-title">Interactive Demo</span>
          <span class="demo-duration">{{ formatTime(currentTime) }} / {{ formatTime(totalDuration) }}</span>
        </div>
      </div>

      <!-- Visual Elements -->
      <div class="demo-visual-elements">
        <div 
          v-for="n in 3" 
          :key="n"
          class="floating-particle"
          :style="{ 
            animationDelay: n * 0.5 + 's',
            left: Math.random() * 80 + 10 + '%',
            top: Math.random() * 60 + 20 + '%'
          }"
        ></div>
      </div>
    </div>

    <!-- Completion State -->
    <transition name="completion-fade">
      <div v-if="isCompleted" class="completion-overlay">
        <div class="completion-content">
          <div class="success-icon">
            <svg class="w-16 h-16 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
            </svg>
          </div>
          <h3 class="completion-title">Demo Complete!</h3>
          <p class="completion-message">Your content is now protected and monitored continuously.</p>
          <button 
            @click="resetDemo"
            class="restart-button"
          >
            Restart Demo
          </button>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import { ref, computed, onMounted, onUnmounted } from 'vue'

export default {
  name: 'InteractiveDemo',
  setup() {
    const isPlaying = ref(false)
    const isCompleted = ref(false)
    const currentStep = ref(0)
    const currentTime = ref(0)
    const totalDuration = ref(30) // 30 seconds total
    let animationInterval = null

    const demoSteps = ref([
      {
        title: 'Upload Content',
        description: 'Securely upload your digital content',
        icon: 'UploadIcon',
        duration: 8
      },
      {
        title: 'AI Scanning',
        description: 'Our AI scans the web for unauthorized copies',
        icon: 'ScanIcon',
        duration: 12
      },
      {
        title: 'Take Action',
        description: 'Automated takedown requests are sent',
        icon: 'ActionIcon',
        duration: 10
      }
    ])

    const progress = computed(() => {
      return (currentTime.value / totalDuration.value) * 100
    })

    const toggleDemo = () => {
      if (isCompleted.value) {
        resetDemo()
        return
      }

      isPlaying.value = !isPlaying.value
      
      if (isPlaying.value) {
        startDemo()
      } else {
        pauseDemo()
      }
    }

    const startDemo = () => {
      animationInterval = setInterval(() => {
        currentTime.value += 0.1
        
        // Update current step based on time
        let stepTime = 0
        for (let i = 0; i < demoSteps.value.length; i++) {
          stepTime += demoSteps.value[i].duration
          if (currentTime.value < stepTime) {
            currentStep.value = i
            break
          }
        }

        // Check if demo is complete
        if (currentTime.value >= totalDuration.value) {
          completeDemo()
        }
      }, 100)
    }

    const pauseDemo = () => {
      if (animationInterval) {
        clearInterval(animationInterval)
        animationInterval = null
      }
    }

    const completeDemo = () => {
      isPlaying.value = false
      isCompleted.value = true
      pauseDemo()
      currentStep.value = demoSteps.value.length
    }

    const resetDemo = () => {
      isPlaying.value = false
      isCompleted.value = false
      currentStep.value = 0
      currentTime.value = 0
      pauseDemo()
    }

    const formatTime = (seconds) => {
      const mins = Math.floor(seconds / 60)
      const secs = Math.floor(seconds % 60)
      return `${mins}:${secs.toString().padStart(2, '0')}`
    }

    onUnmounted(() => {
      pauseDemo()
    })

    return {
      isPlaying,
      isCompleted,
      currentStep,
      currentTime,
      totalDuration,
      demoSteps,
      progress,
      toggleDemo,
      resetDemo,
      formatTime
    }
  }
}
</script>

<style scoped>
.interactive-demo-container {
  position: relative;
  width: 100%;
  height: 100%;
  min-height: 400px;
}

.demo-interface {
  position: relative;
  width: 100%;
  height: 100%;
  background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
  border-radius: 1rem;
  padding: 2rem;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  overflow: hidden;
}

.demo-interface:hover {
  transform: translateY(-2px);
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
}

.demo-interface.is-playing {
  background: linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%);
  box-shadow: 0 0 30px rgba(59, 130, 246, 0.3);
}

.demo-interface.is-completed {
  background: linear-gradient(135deg, #dcfce7 0%, #bbf7d0 100%);
  box-shadow: 0 0 30px rgba(34, 197, 94, 0.3);
}

/* Progress Bar */
.progress-container {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 4px;
  background: rgba(255, 255, 255, 0.3);
  border-radius: 1rem 1rem 0 0;
  overflow: hidden;
}

.progress-bar {
  height: 100%;
  background: linear-gradient(90deg, #3b82f6, #8b5cf6);
  transition: width 0.1s ease-out;
  border-radius: inherit;
}

/* Demo Steps */
.demo-steps {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  margin-top: 2rem;
}

.demo-step {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem;
  border-radius: 0.75rem;
  background: rgba(255, 255, 255, 0.5);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.3);
  transition: all 0.3s ease;
  opacity: 0.6;
}

.demo-step.active {
  opacity: 1;
  background: rgba(255, 255, 255, 0.8);
  border-color: #3b82f6;
  transform: translateX(8px);
}

.demo-step.completed {
  opacity: 0.8;
  background: rgba(34, 197, 94, 0.1);
  border-color: #22c55e;
}

.step-icon {
  flex-shrink: 0;
  width: 3rem;
  height: 3rem;
  background: linear-gradient(135deg, #3b82f6, #8b5cf6);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  transition: all 0.3s ease;
}

.demo-step.completed .step-icon {
  background: linear-gradient(135deg, #22c55e, #16a34a);
}

.step-content {
  flex: 1;
}

.step-title {
  font-weight: 600;
  font-size: 1.1rem;
  color: #1f2937;
  margin-bottom: 0.25rem;
}

.step-description {
  color: #6b7280;
  font-size: 0.9rem;
  line-height: 1.4;
}

.step-indicator {
  flex-shrink: 0;
}

.indicator-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #d1d5db;
  transition: all 0.3s ease;
}

.demo-step.active .indicator-dot {
  background: #3b82f6;
  box-shadow: 0 0 10px rgba(59, 130, 246, 0.5);
}

.demo-step.completed .indicator-dot {
  background: #22c55e;
}

/* Demo Controls */
.demo-controls {
  position: absolute;
  bottom: 2rem;
  left: 2rem;
  right: 2rem;
  display: flex;
  align-items: center;
  gap: 1rem;
}

.play-button {
  width: 4rem;
  height: 4rem;
  border-radius: 50%;
  background: linear-gradient(135deg, #3b82f6, #8b5cf6);
  color: white;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  box-shadow: 0 8px 20px rgba(59, 130, 246, 0.3);
}

.play-button:hover {
  transform: scale(1.05);
  box-shadow: 0 12px 25px rgba(59, 130, 246, 0.4);
}

.play-icon, .pause-icon {
  width: 1.5rem;
  height: 1.5rem;
  margin-left: 2px;
}

.demo-info {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.demo-title {
  font-weight: 600;
  color: #1f2937;
}

.demo-duration {
  font-size: 0.875rem;
  color: #6b7280;
  font-family: 'JetBrains Mono', monospace;
}

/* Visual Elements */
.demo-visual-elements {
  position: absolute;
  inset: 0;
  pointer-events: none;
  overflow: hidden;
}

.floating-particle {
  position: absolute;
  width: 6px;
  height: 6px;
  background: linear-gradient(45deg, #3b82f6, #8b5cf6);
  border-radius: 50%;
  animation: float-particle 4s ease-in-out infinite;
}

@keyframes float-particle {
  0%, 100% { 
    transform: translateY(0) scale(1);
    opacity: 0.6;
  }
  50% { 
    transform: translateY(-20px) scale(1.2);
    opacity: 1;
  }
}

/* Completion Overlay */
.completion-overlay {
  position: absolute;
  inset: 0;
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(10px);
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: inherit;
}

.completion-content {
  text-align: center;
  padding: 2rem;
}

.success-icon {
  margin: 0 auto 1rem;
  width: 4rem;
  height: 4rem;
  background: #dcfce7;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.completion-title {
  font-size: 1.5rem;
  font-weight: 700;
  color: #1f2937;
  margin-bottom: 0.5rem;
}

.completion-message {
  color: #6b7280;
  margin-bottom: 2rem;
  line-height: 1.6;
}

.restart-button {
  background: linear-gradient(135deg, #3b82f6, #8b5cf6);
  color: white;
  border: none;
  padding: 0.75rem 2rem;
  border-radius: 0.5rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
}

.restart-button:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 20px rgba(59, 130, 246, 0.3);
}

/* Transitions */
.completion-fade-enter-active,
.completion-fade-leave-active {
  transition: all 0.5s ease;
}

.completion-fade-enter-from,
.completion-fade-leave-to {
  opacity: 0;
  transform: scale(0.9);
}

/* Responsive Design */
@media (max-width: 768px) {
  .demo-interface {
    padding: 1.5rem;
  }
  
  .demo-steps {
    gap: 1rem;
  }
  
  .demo-step {
    padding: 0.75rem;
  }
  
  .step-icon {
    width: 2.5rem;
    height: 2.5rem;
  }
  
  .demo-controls {
    bottom: 1.5rem;
    left: 1.5rem;
    right: 1.5rem;
  }
  
  .play-button {
    width: 3rem;
    height: 3rem;
  }
}

/* Accessibility */
@media (prefers-reduced-motion: reduce) {
  .floating-particle {
    animation: none;
  }
  
  .demo-interface,
  .demo-step,
  .play-button,
  .restart-button {
    transition: none;
  }
}
</style>